import { InferSelectModel } from "drizzle-orm";
import {
  bigserial,
  boolean,
  char,
  index,
  inet,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  serial,
  smallint,
  text,
  time,
  timestamp,
  unique,
} from "drizzle-orm/pg-core";

export const users = pgTable(
  "users",
  {
    id: serial("id").primaryKey().notNull(),
    login: text("login").notNull(),
    pswd: text("pswd"),
    email: text("email"),
    discordId: text("discord_id"),
    timezone: text("timezone"),
    createdAt: timestamp("createdAt", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    avatar: text("avatar"),
    isAdmin: boolean("is_admin").default(false),
    isBanned: boolean("is_banned").default(false),
  },
  (table) => {
    return [unique("users_login_key").on(table.login)];
  }
);
export type UserModel = InferSelectModel<typeof users>;

export const userDiscords = pgTable(
  "user_discords",
  {
    userId: integer("user_id").notNull(),
    discordId: text("discord_id").notNull(),
    username: text("username").notNull(),
    email: text("email"),
    avatar: text("avatar"),
    verified: boolean("verified").default(false),
  },
  (table) => {
    return [
      unique("user_discords_user_id_key").on(table.userId),
      unique("user_discords_discord_id_key").on(table.discordId),
    ];
  }
);

export const commentVotes = pgTable(
  "comment_votes",
  {
    id: serial("id").primaryKey().notNull(),
    commentId: integer("comment_id")
      .notNull()
      .references(() => comments.id, { onDelete: "cascade" }),
    userId: integer("user_id").notNull(),
    rating: integer("rating").notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "string",
    }).defaultNow(),
  },
  (table) => {
    return [
      unique("comment_votes_comment_id_user_id_key").on(
        table.commentId,
        table.userId
      ),
    ];
  }
);

export const mdRevisions = pgTable("md_revisions", {
  id: serial("id").primaryKey().notNull(),
  pageId: integer("page_id").notNull(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "set null" }),
  prevRevisionId: integer("prev_revision_id"),
  titleEn: text("title_en"),
  contentEn: text("content_en"),
  titleRu: text("title_ru"),
  contentRu: text("content_ru"),
  isDeleted: boolean("is_deleted").default(false),
  images: integer("images").array(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const mdPages = pgTable(
  "md_pages",
  {
    id: serial("id").primaryKey().notNull(),
    game: text("game").array(),
    revisionId: integer("revision_id"),
    wikiSlug: text("wiki_slug"),
    questId: integer("quest_id"),
    itemName: text("item_name"),
    npcName: text("npc_name"),
    locName: text("loc_name"),
    skillId: integer("skill_id"),
    draftId: integer("draft_id"),
    isDeleted: boolean("is_deleted").default(false),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => {
    return [unique("md_pages_slug_key").on(table.wikiSlug)];
  }
);

export type MdPageModel = InferSelectModel<typeof mdPages>;

export const userPages = pgTable("user_pages", {
  id: serial("id").primaryKey().notNull(),
  userId: integer("user_id").notNull(),
  public: boolean("public").default(false).notNull(),
  isDeleted: boolean("is_deleted").default(false),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const mdImages = pgTable("md_images", {
  id: serial("id").primaryKey().notNull(),
  pageId: integer("page_id").notNull(),
  userId: integer("user_id").notNull(),
  path: text("path").notNull(),
  width: integer("width").notNull(),
  height: integer("height").notNull(),
  isDeleted: boolean("is_deleted").default(false),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const loot = pgTable("loot", {
  id: serial("id").primaryKey().notNull(),
  characterId: integer("character_id").notNull(),
  timestamp: timestamp("timestamp", {
    withTimezone: true,
    mode: "string",
  }).notNull(),
  itemId: integer("item_id").notNull(),
  count: integer("count").default(1).notNull(),
});

export const TkSessionStateEnum = pgEnum("tk_session_state", [
  "active",
  "done",
  "failed",
  "saved",
]);

export const tkSessions = pgTable("tk_sessions", {
  id: bigserial("id", { mode: "number" }).primaryKey().notNull(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  state: TkSessionStateEnum("state").notNull(),
  loginServer: text("login_server").notNull(),
  charName: text("char_name").notNull(),
  startedAt: timestamp("started_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  endedAt: timestamp("ended_at", { withTimezone: true }),
});

export const tkChars = pgTable(
  "tk_chars",
  {
    id: serial("id").primaryKey().notNull(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id),
    loginServer: text("login_server"),
    charName: text("char_name").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => {
    return [
      unique("characters_unique_key").on(
        table.userId,
        table.loginServer,
        table.charName
      ),
    ];
  }
);

export const tkScannerCursors = pgTable("tk_scanner_cursors", {
  scannerName: text("scanner_name").notNull().primaryKey(),
  minSessionId: integer("min_session_id").notNull(),
  maxSessionId: integer("max_session_id").notNull(),
});

export const tkLoot = pgTable("tk_loot", {
  id: serial("id").primaryKey().notNull(),
  characterId: integer("character_id"),
  timestamp: timestamp("timestamp", { withTimezone: true }),
  itemId: integer("item_id"),
  count: integer("count"),
});

export const userSessions = pgTable("user_sessions", {
  id: text("id").primaryKey().notNull(),
  userId: integer("user_id").notNull(),
  data: jsonb("data"),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  usedAt: timestamp("used_at", { withTimezone: true }),
  expiresAt: timestamp("expires_at", {
    withTimezone: true,
  }).notNull(),
});
export type SessionModel = InferSelectModel<typeof userSessions>;

export const subscriptions = pgTable(
  "subscriptions",
  {
    id: serial("id").primaryKey().notNull(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id),
    startsAt: timestamp("starts_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    endsAt: timestamp("ends_at", {
      withTimezone: true,
      mode: "string",
    }).notNull(),
    key: text("key").notNull(),
  },
  (table) => {
    return [unique("subscriptions_key_key").on(table.key)];
  }
);

// export const tkCharStats = pgTable("tk_char_stats", {
//   id: bigserial("id", { mode: "number" }).primaryKey().notNull(),
//   charId: integer("char_id")
//     .notNull()
//     .references(() => tkChars.id),
//   level: smallint("level"),
//   race: smallint("race"),
//   classId: smallint("class_id"),
//   xp: integer("xp"),
//   sp: integer("sp"),
//   curHp: integer("cur_hp"),
//   maxHp: integer("max_hp"),
//   curMp: integer("cur_mp"),
//   maxMp: integer("max_mp"),
//   curCp: integer("cur_cp"),
//   maxCp: integer("max_cp"),
//   patk: integer("patk"),
//   pdef: integer("pdef"),
//   accuracy: integer("accuracy"),
//   critical: integer("critical"),
//   atkSpd: integer("atk_spd"),
//   matk: integer("matk"),
//   mdef: integer("mdef"),
//   evasion: integer("evasion"),
//   speed: integer("speed"),
//   castSpd: integer("cast_spd"),
//   str: integer("str"),
//   int: integer("int"),
//   dex: integer("dex"),
//   wit: integer("wit"),
//   con: integer("con"),
//   men: integer("men"),
//   karma: integer("karma"),
//   pvp: integer("pvp"),
//   pk: integer("pk"),
//   evalScore: integer("eval_score"),
//   recRemaining: integer("rec_remaining"),
// });

export const pages = pgTable("pages", {
  id: serial("id").primaryKey().notNull(),
  content: text("content").notNull(),
  userId: integer("user_id").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  title: text("title").notNull(),
  lang: char("lang", { length: 2 }).notNull(),
  game: text("game").array(),
  objType: char("obj_type", { length: 25 }),
  objId: integer("obj_id"),
});

export const payments = pgTable("payments", {
  id: serial("id").primaryKey().notNull(),
  userId: integer("user_id").notNull(),
  telegramUserId: text("telegram_user_id").notNull(),
  telegramPaymentChargeId: text("telegram_payment_charge_id"),
  currency: text("currency").notNull(),
  amount: integer("amount").notNull(),
  status: text("status").notNull(),
  description: text("description"),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  updatedAt: time("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const comments = pgTable("comments", {
  id: serial("id").primaryKey().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  content: text("content").notNull(),
  userId: integer("user_id").notNull(),
  objType: text("obj_type").notNull(),
  objUname: text("obj_uname").notNull(),
  isDeleted: boolean("is_deleted").default(false).notNull(),
  lang: text("lang").notNull(),
  game: text("game").notNull(),
  parentId: integer("parent_id"),
});

export const charInventory = pgTable(
  "char_inventory",
  {
    characterId: integer("character_id").notNull(),
    // .references(() => tkChars.id)
    itemId: integer("item_id").notNull(),
    count: integer("count").notNull(),
  },
  (table) => {
    return [primaryKey({ columns: [table.characterId, table.itemId] })];
  }
);

export const tkDeriverSessions = pgTable(
  "tk_deriver_sessions",
  {
    sessionId: integer("session_id").notNull(),
    deriverId: text("deriver_id").notNull(),
    userId: integer("user_id").notNull(),
    revision: integer("revision").notNull(),
    loginServer: text("login_server").notNull(),
    characterId: integer("character_id"),
  },
  (table) => {
    return [primaryKey({ columns: [table.sessionId, table.deriverId] })];
  }
);

//
// Old unused tables.
//

export const tkCharacters = pgTable(
  "_tk_characters",
  {
    id: serial("id").primaryKey().notNull(),
    userId: integer("user_id").notNull(),
    loginServer: text("login_server").notNull(),
    name: text("name").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => {
    return [
      unique("tk_characters_user_id_login_server_name_key").on(
        table.userId,
        table.loginServer,
        table.name
      ),
    ];
  }
);

export const trkrSessions = pgTable("trkr_sessions", {
  sessionId: integer("session_id").primaryKey().notNull(),
  userId: integer("user_id").notNull(),
  revision: smallint("revision").notNull(),
  characterId: integer("character_id"),
});

// ANAL
export const pageViews = pgTable(
  "page_views",
  {
    id: serial("id").primaryKey(),
    userAgent: text("user_agent"),
    referer: text("referer"),
    ip: inet("ip"),
    path: text("path"),
    gver: text("gver"),
    lang: text("lang"),
    userId: integer("user_id"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("created_at_path_idx").on(table.createdAt, table.path),
    index("created_at_idx").on(table.createdAt),
  ]
);
